<template>
  <div class="mobile-container">
    <div class="mobile-content">
      <div class="content-container">
        <div class="header">
          <div class="header-content">
            <h1>
              {{ currentGroup.competencyGrouping }}
              <span class="page-number"
                >{{ currentIndex + 1 }} of {{ totalGroups }}</span
              >
            </h1>
            <button
              class="view-toggle"
              @click="toggleViewMode"
              :title="
                viewMode === 'radio'
                  ? 'Switch to button view'
                  : 'Switch to radio view'
              "
            >
              <i
                :class="
                  viewMode === 'radio' ? 'fas fa-eye' : 'fas fa-eye-slash'
                "
              ></i>
            </button>
          </div>
          <p class="group-description">
            {{ currentGroup.competencyGroupingDescription }}
          </p>
        </div>

        <div class="competencies-list">
          <div
            v-for="(competency, index) in currentGroup.competencies"
            :key="index"
            class="competency-item"
          >
            <div class="competency-header">
              <h3>{{ capitalizeEachWord(competency.competencyTitle) }}</h3>
              <button class="info-button" @click="showDescription(competency)">
                <i class="fas fa-info-circle"></i>
              </button>
            </div>

            <div v-if="viewMode === 'radio'" class="proficiency-checkboxes">
              <div
                v-for="(proficiency, pIndex) in proficiencies"
                :key="pIndex"
                class="checkbox-item"
              >
                <input
                  type="checkbox"
                  :id="`${competency.competencyTitle}-${pIndex}`"
                  :checked="
                    isChecked(
                      competency.competencyTitle,
                      proficiency.proficiencyTitle
                    )
                  "
                  @change="
                    handleCheckboxChange(
                      competency.competencyTitle,
                      proficiency.proficiencyTitle
                    )
                  "
                  :title="`Select your proficiency level for ${competency.competencyTitle}`"
                />
                <label :for="`${competency.competencyTitle}-${pIndex}`">
                  {{ proficiency.proficiencyTitle }}
                </label>
              </div>
            </div>

            <div v-else class="proficiency-buttons">
              <button
                v-for="(proficiency, pIndex) in proficiencies"
                :key="pIndex"
                class="proficiency-button"
                :class="{
                  active: isChecked(
                    competency.competencyTitle,
                    proficiency.proficiencyTitle
                  ),
                }"
                @click="
                  handleButtonClick(
                    competency.competencyTitle,
                    proficiency.proficiencyTitle
                  )
                "
              >
                {{ proficiency.proficiencyTitle }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Navigation Buttons -->
      <div class="navigation">
        <button
          @click="prevGroup"
          :disabled="currentIndex === 0"
          class="nav-button"
        >
          Previous
        </button>
        <button
          v-if="currentIndex < totalGroups - 1"
          @click="nextGroup"
          class="nav-button"
        >
          Next
        </button>
      </div>
      <div class="review">
        <button @click="goToSummary" class="nav-button review-button">
          Review
        </button>
      </div>

      <!-- Progress Bar -->
      <div class="progress-container">
        <div
          class="progress-bar"
          :style="{ width: `${progressPercentage}%` }"
        ></div>
        <span class="progress-text"
          >Page {{ currentIndex + 1 }} of {{ totalGroups }}</span
        >
      </div>

      <!-- Save Status -->
      <div class="save-status" :class="{ saving: isSaving, saved: isSaved }">
        {{ saveStatusText }}
      </div>
    </div>

    <!-- Description Popup -->
    <div v-if="showPopup" class="popup-overlay" @click="closePopup">
      <div class="popup-content" @click.stop>
        <div class="popup-header">
          <h3>{{ capitalizeEachWord(selectedCompetency?.competencyTitle) }}</h3>
          <button class="close-button" @click="closePopup">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <p class="popup-description">
          {{ selectedCompetency?.competencyTitleDescription }}
        </p>
      </div>
    </div>

    <!-- Summary Modal -->
    <div
      v-if="showSummaryModal"
      class="summary-modal-overlay"
      @click="closeSummaryModal"
    >
      <div class="summary-modal-content" @click.stop>
        <div class="summary-modal-header">
          <h2>Review Your Competency Selections</h2>
          <button class="close-button" @click="closeSummaryModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="summary-content">
          <div v-if="summaryData.length === 0" class="no-selections">
            <p>No competency selections made yet.</p>
            <p>
              Please go back and select your proficiency levels for the
              competencies.
            </p>
          </div>

          <div v-else class="selections-list">
            <div
              v-for="(item, index) in summaryData"
              :key="index"
              class="selection-item"
            >
              <div class="selection-header">
                <h4>{{ item.competencyTitle }}</h4>
                <span class="proficiency-badge">{{
                  item.proficiencyTitle
                }}</span>
              </div>
              <p class="group-name">{{ item.groupName }}</p>
            </div>
          </div>
        </div>

        <div class="summary-modal-actions">
          <button
            class="action-button close-action-button"
            @click="closeSummaryModal"
          >
            Close
          </button>
          <button
            class="action-button proceed-action-button"
            @click="proceedFromSummary"
            :disabled="summaryData.length === 0"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { competencyGroups, proficiencies } from "../stores/constants";

export default {
  name: "MobileView",
  setup() {
    const router = useRouter();
    const currentIndex = ref(0);
    const selectedProficiencies = ref({});
    const showPopup = ref(false);
    const selectedCompetency = ref(null);
    const isSaving = ref(false);
    const isSaved = ref(false);
    const saveTimeout = ref(null);
    const viewMode = ref("button"); // 'radio' or 'button'
    const showSummaryModal = ref(false);

    const totalGroups = computed(() => competencyGroups.length);
    const currentGroup = computed(() => competencyGroups[currentIndex.value]);

    // Calculate progress based on current page
    const progressPercentage = computed(() => {
      return Math.round(((currentIndex.value + 1) / totalGroups.value) * 100);
    });

    const saveStatusText = computed(() => {
      if (isSaving.value) return "Saving...";
      if (isSaved.value) return "Progress saved";
      return "";
    });

    const scrollToTop = (behavior = "smooth") => {
       document.querySelector(".game-survey").scrollTo({ top: 0, left: 0 })
    };

    // Save progress to localStorage
    const saveProgress = () => {
      isSaving.value = true;
      isSaved.value = false;

      const progress = {
        currentIndex: currentIndex.value,
        selectedProficiencies: selectedProficiencies.value,
        lastSaved: new Date().toISOString(),
        timestamp: new Date().toISOString(), // Add timestamp for retention tracking
      };

      localStorage.setItem("surveyProgress", JSON.stringify(progress));

      // Simulate saving delay
      clearTimeout(saveTimeout.value);
      saveTimeout.value = setTimeout(() => {
        isSaving.value = false;
        isSaved.value = true;
        setTimeout(() => {
          isSaved.value = false;
        }, 2000);
      }, 500);
    };

    // Load progress from localStorage
    const loadProgress = () => {
      const savedProgress = localStorage.getItem("surveyProgress");
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          currentIndex.value = progress.currentIndex;
          selectedProficiencies.value = progress.selectedProficiencies;
        } catch (error) {
          console.error("Error loading saved progress:", error);
        }
      }
    };

    const isChecked = (competencyTitle, proficiencyTitle) => {
      return selectedProficiencies.value[competencyTitle] === proficiencyTitle;
    };

    const handleCheckboxChange = (competencyTitle, proficiencyTitle) => {
      if (isChecked(competencyTitle, proficiencyTitle)) {
        delete selectedProficiencies.value[competencyTitle];
      } else {
        selectedProficiencies.value[competencyTitle] = proficiencyTitle;
      }
      saveProgress();
    };

    const showDescription = (competency) => {
      selectedCompetency.value = competency;
      showPopup.value = true;
    };

    const closePopup = () => {
      showPopup.value = false;
      selectedCompetency.value = null;
    };

    const nextGroup = () => {
      if (currentIndex.value < totalGroups.value - 1) {
        currentIndex.value++;
        saveProgress();
        setTimeout(() => {
          scrollToTop("smooth");
        }, 100);
      }
    };

    const prevGroup = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
        saveProgress();
        setTimeout(() => {
          scrollToTop("smooth");
        }, 100);
      }
    };

    const goToSummary = () => {
      showSummaryModal.value = true;
    };

    const toggleViewMode = () => {
      viewMode.value = viewMode.value === "radio" ? "button" : "radio";
    };

    const handleButtonClick = (competencyTitle, proficiencyTitle) => {
      if (isChecked(competencyTitle, proficiencyTitle)) {
        delete selectedProficiencies.value[competencyTitle];
      } else {
        selectedProficiencies.value[competencyTitle] = proficiencyTitle;
      }
      saveProgress();
    };

    const closeSummaryModal = () => {
      showSummaryModal.value = false;
    };

    const proceedFromSummary = () => {
      showSummaryModal.value = false;

      // Save the competency selections to localStorage for the next stage
      const competencyData = {
        selectedProficiencies: selectedProficiencies.value,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem(
        "competencySelections",
        JSON.stringify(competencyData)
      );

      // Navigate to the next stage in the survey (Professional Experience)
      router.push("/survey/experience");
    };

    // Computed property to get summary data
    const summaryData = computed(() => {
      const summary = [];
      competencyGroups.forEach((group, groupIndex) => {
        group.competencies.forEach((competency) => {
          const selectedProficiency =
            selectedProficiencies.value[competency.competencyTitle];
          if (selectedProficiency) {
            summary.push({
              groupName: group.competencyGrouping,
              competencyTitle: competency.competencyTitle,
              proficiencyTitle: selectedProficiency,
              groupIndex,
            });
          }
        });
      });
      return summary;
    });

    // Watch for changes in currentIndex and save progress
    watch(currentIndex, () => {
      saveProgress();
      scrollToTop("smooth");
    });

    // Load saved progress when component is mounted
    onMounted(() => {
      loadProgress();
      scrollToTop("smooth");
    });

    const capitalizeEachWord = (text) => {
       return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return {
      currentIndex,
      totalGroups,
      currentGroup,
      proficiencies,
      isChecked,
      handleCheckboxChange,
      nextGroup,
      prevGroup,
      showPopup,
      selectedCompetency,
      showDescription,
      closePopup,
      progressPercentage,
      isSaving,
      isSaved,
      saveStatusText,
      goToSummary,
      viewMode,
      toggleViewMode,
      handleButtonClick,
      showSummaryModal,
      closeSummaryModal,
      proceedFromSummary,
      summaryData,
      capitalizeEachWord
    };
  },
};
</script>
<style>
</style>
<style scoped>
.mobile-container {
  width: 100%;
  /* Let the parent survey container handle scrolling (more reliable on real mobile). */
  overflow: visible;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  background-color: #f5f5f5;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  overscroll-behavior: auto;
}

.mobile-content {
  width: 100%;
  max-width: 100%;
  border-radius: 0;
  box-shadow: none;
  padding: calc(15px + env(safe-area-inset-top)) 15px
    calc(15px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  position: relative;
  -webkit-overflow-scrolling: touch;
  /* Layout: header + list + nav/review/progress */
  display: flex;
  flex-direction: column;
  overflow: visible;
  flex: 1;
  min-height: 0;
}

.content-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* critical so the list can scroll inside */
}

.header {
  text-align: center;
  margin-bottom: 15px;
  padding: 0 5px;
  position: relative;
  z-index: 1;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

h1 {
  font-size: 20px;
  color: #333;
  margin-bottom: 8px;
  word-wrap: break-word;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.page-number {
  font-size: 16px;
  color: #666;
  background-color: #f0f0f0;
  padding: 2px 8px;
  border-radius: 12px;
  white-space: nowrap;
}

.group-description {
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  word-wrap: break-word;
}

.competencies-list {
  margin-bottom: 15px;
  /* Allow parent container to scroll instead of relying on nested overflow regions */
  flex: 1;
  min-height: 0;
  overflow: visible;
}

.competency-item {
  margin-bottom: 15px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-sizing: border-box;
}

.competency-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  width: 100%;
}

.competency-header h3 {
  font-size: 16px;
  color: #333;
  margin: 0;
  flex: 1;
  word-wrap: break-word;
  padding-right: 8px;
  min-width: 0;
  text-align: left;
}

.info-button {
  background: none;
  border: none;
  color: #42b883;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.info-button i {
  display: block;
  line-height: 1;
}

.proficiency-checkboxes {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 44px;
  padding: 0.5rem 0;
}

.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.checkbox-item label {
  font-size: 14px;
  color: #333;
  word-wrap: break-word;
  line-height: 1.4;
  cursor: pointer;
}

.navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 15px 0;
  padding: 0 5px;
}
.review {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 15px 0;
  padding: 0 5px;
  width: 100%;
}

.nav-button {
  padding: 10px 15px;
  background-color: #42b883;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  flex: 1;
  margin: 0 5px;
}

.nav-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.progress-container {
  margin: 0;
  margin-top: 15px;
  position: relative;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
  z-index: 1;
}

.progress-bar {
  height: 100%;
  background-color: #42b883;
  transition: width 0.3s ease;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #333;
  font-size: 12px;
  font-weight: bold;
  text-shadow: 0 0 2px white;
}

.save-status {
  display: none;
}

.save-status.saving {
  display: none;
}

.save-status.saved {
  display: none;
}

/* Popup Styles */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 15px;
  box-sizing: border-box;
}

.popup-content {
  background-color: white;
  border-radius: 10px;
  padding: 15px;
  width: 100%;
  max-width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.popup-header {
  display: flex;
  margin-bottom: 12px;
}

.popup-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
  width: 90%;
}

.close-button {
  background: none;
  border: none;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-button:hover {
  color: #495057;
  background-color: #f8f9fa;
}

.popup-description {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  word-wrap: break-word;
}

.review-button {
  border: 1px solid #28a745;
  background-color: white;
  color: #28a745;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 10px 15px;
}

.desktop-message {
  display: none;
  text-align: center;
  padding: 40px;
  max-width: 600px;
  margin: 0 auto;
}

.desktop-message h1 {
  font-size: 24px;
  color: #333;
  margin-bottom: 20px;
}

.desktop-message p {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
}

@media (min-width: 1200) {
  .mobile-container {
    display: block;
  }

  .mobile-content {
    display: none;
  }
}

.view-toggle {
  background: none;
  border: none;
  color: #42b883;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: background-color 0.3s;
}

.view-toggle:hover {
  background-color: rgba(66, 184, 131, 0.1);
}

.proficiency-buttons {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.proficiency-button {
  padding: 8px 12px;
  border: 1px solid #42b883;
  border-radius: 6px;
  background-color: white;
  color: #42b883;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;
  white-space: nowrap;
  flex: 1;
  width: 145px;
}

.proficiency-button:hover {
  background-color: rgba(66, 184, 131, 0.1);
}

.proficiency-button.active {
  background-color: #42b883;
  color: white;
}

/* Summary Modal Styles */
.summary-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 15px;
  box-sizing: border-box;
}

.summary-modal-content {
  background-color: white;
  border-radius: 15px;
  width: 100%;
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

.summary-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.summary-modal-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
  font-weight: 600;
}

.summary-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.no-selections {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.no-selections p {
  margin: 10px 0;
  font-size: 16px;
  line-height: 1.5;
}

.selections-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.selection-item {
  background-color: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
  border-left: 4px solid #42b883;
}

.selection-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 10px;
}

.selection-header h4 {
  margin: 0;
  font-size: 16px;
  color: #333;
  font-weight: 600;
  flex: 1;
  word-wrap: break-word;
}

.proficiency-badge {
  background-color: #42b883;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.group-name {
  margin: 0;
  font-size: 14px;
  color: #666;
  font-style: italic;
}

.summary-modal-actions {
  display: flex;
  gap: 15px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.action-button {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-button.close-action-button {
  background-color: #6c757d;
  color: white;
}

.action-button.close-action-button:hover {
  background-color: #5a6268;
}

.action-button.proceed-action-button {
  background-color: #28a745;
  color: white;
}

.action-button.proceed-action-button:hover:not(:disabled) {
  background-color: #218838;
}

.action-button.proceed-action-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
